import { Activity } from 'src/services/activities/activities.types';

import { rootApi } from '../rootApi';
import { EmploymentType } from '../jobs/jobs.types';
import { CandidateProfile } from '../candidates/candidates.types';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  Offer,
  Placement,
  OpenStatus,
  ApplyIntent,
  JobApplication,
  CreateOfferDto,
  PlacementOwner,
  RejectionReason,
  PlacementDocument,
  StageChangeRequest,
  ApplicationInsight,
  CreatePlacementDto,
  GetApplicationsQuery,
  ComplianceSubmission,
  IApplicationListItem,
  ActionOfferRequestProps,
  StageChangeRequestAction,
  StageChangeRequestStatus,
  GetApplicationsListQuery,
  ApplicationContactUpdate,
  CreatePlacmentDocumentDto,
  NotifyOfferCandidateProps,
  ActionResourceRequestProps,
  ApplicationResourceRequest,
  LogOfferActionRequestProps,
  SubmissionVisibilityStatus,
  ApplicationContactResponse,
  InitiateResourceRequestProps,
} from './applications.types';

export const applicationsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    getApplications: builder.query<PaginatedResponse<JobApplication>, GetApplicationsQuery>({
      query: ({ params }) => ({
        url: `/jobs/applications`,
        params,
      }),
      providesTags: ['Applications'],
    }),

    // Lean read path backing the org-wide Applications table. Separate from getApplications, which
    // rebuilds the full per-application payload one row at a time; this returns the flat
    // IApplicationListItem the shared DataList renders.
    getApplicationsLean: builder.query<PaginatedResponse<IApplicationListItem>, GetApplicationsListQuery>({
      query: (data) => ({
        url: `/jobs/applications/list`,
        params: {
          search_query: data.search_query,
          // Per-column search values, serialized as JSON. Omitted when empty.
          ...(data.column_filters && Object.keys(data.column_filters).length
            ? { column_filters: JSON.stringify(data.column_filters) }
            : {}),
          ...(data.my_jobs ? { my_jobs: true } : {}),
          ...data.page,
          ...(data.sortConfig ?? {}),
        },
      }),
      providesTags: ['Applications'],
    }),

    getJobCandidates: builder.query<PaginatedResponse<JobApplication>, GetApplicationsQuery>({
      query: ({ params, jobId }) => ({
        url: `/jobs/${jobId}/candidates`,
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'JobCandidates', id: arg.jobId },
        { type: 'Applications' },
      ],
    }),

    getPendingApplicantsQuery: builder.query<PaginatedResponse<ApplyIntent>, GetApplicationsQuery>({
      query: ({ params, jobId }) => ({
        url: `/jobs/${jobId}/apply-intents`,
        params,
      }),
    }),

    resendApplyIntentVerification: builder.mutation<APIResponse, { applyIntentId: string }>({
      query: ({ applyIntentId }) => ({
        url: `/apply-intents/${applyIntentId}/resend-verification`,
        method: HTTPRequestType.POST,
      }),
    }),

    getJobApplicants: builder.query<PaginatedResponse<JobApplication>, GetApplicationsQuery>({
      query: ({ params, jobId }) => ({
        url: `/jobs/${jobId}/applicants`,
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'JobApplications', id: arg.jobId },
        { type: 'Applications' },
      ],
    }),

    getApplicationActivities: builder.query<
      PaginatedResponse<Activity>,
      { applicationId: string; page: number; perPage: number }
    >({
      query: ({ applicationId, perPage, page }) => ({
        url: `/jobs/applications/${applicationId}/activities`,
        params: {
          page,
          per_page: perPage,
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ApplicationActivities', id: arg.applicationId },
      ],
    }),

    withdrawApplication: builder.mutation<APIResponse, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/jobs/applications/${applicationId}/withdraw`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ApplicationActivities', id: arg.applicationId },
        'Applications',
        'ApplicationRequests',
      ],
    }),

    progressCandidateApplication: builder.mutation<JobApplication, { applicationId: string; data: { stage: string; rejection_context?: { reason: string; feedback: string, should_notify: boolean }; withdrawal_reason_id?: string }; }>({
      query: ({ applicationId, data }) => ({
        url: `/jobs/applications/${applicationId}/progress`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests', 'JobSummary', 'Nudges'],
    }),

    progressToCandidate: builder.mutation<APIResponse, { jobId: string; applicationIds: string[] }>({
      query: ({ jobId, applicationIds }) => ({
        url: `/jobs/${jobId}/applications/progress-to-candidate`,
        method: HTTPRequestType.POST,
        data: { application_ids: applicationIds },
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests', 'JobSummary', 'Nudges']
    }),

    shortlistApplications: builder.mutation<APIResponse, { jobId: string; applicationIds: string[] }>({
      query: ({ jobId, applicationIds }) => ({
        url: `/jobs/${jobId}/applications/shortlist`,
        method: HTTPRequestType.POST,
        data: { application_ids: applicationIds },
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests', 'Nudges'],
    }),

    updateApplicationRating: builder.mutation<APIResponse, { applicationId: string; rating: number }>({
      query: ({ applicationId, rating }) => ({
        url: `/jobs/applications/${applicationId}/rating`,
        method: HTTPRequestType.POST,
        data: { rating },
      }),
      invalidatesTags: ['Applications', 'ConnectionApplications'],
    }),

    updateApplicationOpenStatus: builder.mutation<APIResponse, { applicationId: string; openStatus: OpenStatus }>({
      query: ({ applicationId, openStatus }) => ({
        url: `/jobs/applications/${applicationId}/open-status`,
        method: HTTPRequestType.POST,
        data: { open_status: openStatus },
      }),
      invalidatesTags: ['Applications'],
    }),

    rejectApplications: builder.mutation<APIResponse, { jobId: string, applicationIds: string[], rejectionFeedback: string, rejectionReason: RejectionReason, shouldNotify: boolean }>({
      query: ({ jobId, applicationIds, rejectionFeedback, rejectionReason, shouldNotify }) => ({
        url: `/jobs/${jobId}/applications/reject`,
        method: HTTPRequestType.POST,
        data: {
          application_ids: applicationIds,
          rejection_feedback: rejectionFeedback,
          rejection_reason: rejectionReason,
          should_notify: shouldNotify
        },
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests', 'JobApplications', 'JobCandidates', 'Nudges'],
    }),

    requestRejectApplications: builder.mutation<APIResponse, { jobId: string, applicationIds: string[] }>({
      query: ({ jobId, applicationIds }) => ({
        url: `/jobs/${jobId}/applications/request-rejection`,
        method: HTTPRequestType.POST,
        data: { application_ids: applicationIds },
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests'],
    }),

    getApplicationById: builder.query<JobApplication, string>({
      query: (applicationId) => ({
        url: `/jobs/applications/${applicationId}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Applications', id: arg }]
    }),

    requestApplicationStageChange: builder.mutation<APIResponse, { applicationId: string; requested_stage: string; rejection_reason?: string; rejection_feedback?: string; }>({
      query: ({ applicationId, requested_stage, rejection_reason, rejection_feedback }) => ({
        url: `/jobs/applications/${applicationId}/stage-change-requests`,
        method: HTTPRequestType.POST,
        data: {
          requested_stage,
          rejection_reason,
          rejection_feedback,
        },
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests'],
    }),

    getApplicationStageChangeRequests: builder.query<PaginatedResponse<StageChangeRequest>, { params: { status?: StageChangeRequestStatus } & PageOptions; applicationId: string }>({
      query: ({ params, applicationId }) => ({
        url: `/jobs/applications/${applicationId}/stage-change-requests/incoming`,
        params,
      }),
      providesTags: ['ApplicationRequests'],
    }),

    getApplicationStageChangeRequestById: builder.query<StageChangeRequest, string>({
      query: (requestId) => ({
        url: `/jobs/applications/stage-change-requests/${requestId}`,
      }),
      providesTags: ['ApplicationRequests'],
    }),

    actionStageChangeRequest: builder.mutation<
      APIResponse,
      {
        requestId: string;
        action: StageChangeRequestAction;
        rejection_reason?: string;
        rejection_feedback?: string;
      }
    >({
      query: ({ requestId, action, rejection_feedback, rejection_reason }) => ({
        url: `/jobs/applications/stage-change-requests/${requestId}/action`,
        method: HTTPRequestType.POST,
        data: {
          action,
          rejection_feedback,
          rejection_reason,
        },
      }),
      invalidatesTags: ['ApplicationRequests', 'Applications', 'JobApplications', 'Nudges'],
    }),

    getAllJobStageChangeRequests: builder.query<
      PaginatedResponse<StageChangeRequest>,
      { jobId: string; params: { status?: StageChangeRequestStatus } & PageOptions }
    >({
      query: ({ jobId, params }) => ({
        url: `/jobs/${jobId}/stage-change-requests/incoming`,
        params,
      }),
      providesTags: ['ApplicationRequests'],
    }),

    createOffer: builder.mutation<Offer, { applicationId: string; offer: CreateOfferDto }>({
      query: ({ applicationId, offer }) => ({
        url: `/jobs/applications/${applicationId}/offers`,
        method: HTTPRequestType.POST,
        data: offer,
      }),
      invalidatesTags: ['Applications', 'ApplicationRequests', 'Offers'],
    }),

    updateOffer: builder.mutation<Offer, { offerId: string; offerData: CreateOfferDto }>({
      query: ({ offerId, offerData }) => ({
        url: `/jobs/applications/offers/${offerId as string}`,
        method: HTTPRequestType.PUT,
        data: offerData,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers', id: arg.offerId },
        { type: 'Applications' },
        { type: 'ApplicationRequests' },
        { type: 'JobOffers' },
      ],
    }),

    getApplicationOffers: builder.query<PaginatedResponse<Offer>, { applicationId: string; params: PageOptions }>({
      query: ({ applicationId, params }) => ({
        url: `/jobs/applications/${applicationId}/offers`,
        params,
      }),
      providesTags: ['Offers'],
    }),

    getApplicationCandidateProfile: builder.query<CandidateProfile, string>({
      query: (applicationId) => ({
        url: `/jobs/applications/${applicationId}/candidate-profile`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ApplicationProfile', id: arg }]
    }),
    updateApplicationCandidateProfile: builder.mutation<APIResponse, { applicationId: string; data: Partial<CandidateProfile> }>({
      query: ({ applicationId, data }) => ({
        url: `/jobs/applications/${applicationId}/candidate-profile`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ApplicationProfile', id: arg.applicationId }, { type: 'Profile', id: arg.applicationId }]
    }),
    // Also writes to the candidate record (an empty field filled, custom fields saved), so its views
    // refresh too. The bare Applications tag reaches the job's applications list as well.
    updateApplicationContact: builder.mutation<ApplicationContactResponse, { applicationId: string; orgCandidateId?: string; data: ApplicationContactUpdate }>({
      query: ({ applicationId, data }) => ({
        url: `/jobs/applications/${applicationId}/contact`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        'Applications',
        'CandidateProfile',
        { type: 'ApplicationProfile', id: arg.applicationId },
        ...(arg.orgCandidateId
          ? [
            { type: 'OrgCandidates' as const, id: arg.orgCandidateId },
            { type: 'CustomFields' as const, id: `candidate-${arg.orgCandidateId}` },
          ]
          : []),
      ]
    }),

    getOfferById: builder.query<Offer, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/jobs/applications/offers/${offerId}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Offers', id: arg.offerId }],
    }),

    getMyOffers: builder.query<PaginatedResponse<Offer>, PageOptions>({
      query: (params) => ({
        url: `/jobs/applications/offers`,
        params,
      }),
      providesTags: ['Offers'],
    }),

    getOffersByJobId: builder.query<
      PaginatedResponse<Offer>,
      { jobId: string; params: PageOptions }
    >({
      query: ({ jobId, params }) => ({
        url: `/jobs/${jobId}/applications/offers`,
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobOffers', id: arg.jobId }],
    }),

    actionOffer: builder.mutation<Placement | null, ActionOfferRequestProps>({
      query: ({ offerId, action, documents }) => ({
        url: `/jobs/applications/offers/${offerId}/action`,
        method: HTTPRequestType.POST,
        data: {
          action,
          documents
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers' },
        { type: 'Offers', id: arg.offerId },
        { type: 'Applications' },
        { type: 'JobOffers' },
      ],
    }),
    logOfferAction: builder.mutation<void, LogOfferActionRequestProps>({
      query: ({ offerId, action, documents }) => ({
        url: `/jobs/applications/offers/${offerId}/log-action`,
        method: HTTPRequestType.POST,
        data: {
          action,
          documents
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers' },
        { type: 'Offers', id: arg.offerId },
        { type: 'Applications' },
        { type: 'JobOffers' },
      ],
    }),

    notifyOfferCandidate: builder.mutation<void, NotifyOfferCandidateProps>({
      query: ({ offerId, content, subject }) => ({
        url: `/jobs/applications/offers/${offerId}/notify-candidate`,
        method: HTTPRequestType.POST,
        data: {
          content,
          subject
        },
      }),
    }),

    approveOffer: builder.mutation<APIResponse, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/jobs/applications/offers/${offerId}/approve`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers', id: arg.offerId },
        { type: 'Offers' },
        { type: 'Applications' },
        { type: 'JobOffers' },
      ],
    }),

    requestOfferChanges: builder.mutation<APIResponse, { offerId: string; notes: string }>({
      query: ({ offerId, notes }) => ({
        url: `/jobs/applications/offers/${offerId}/request-changes`,
        method: HTTPRequestType.POST,
        data: { notes },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers', id: arg.offerId },
        { type: 'Offers' },
        { type: 'Applications' },
        { type: 'JobOffers' },
      ],
    }),

    rescindOffer: builder.mutation<APIResponse, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/jobs/applications/offers/${offerId}/rescind`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Offers', id: arg.offerId },
        { type: 'Offers' },
        { type: 'Applications' },
        { type: 'ApplicationRequests' },
        { type: 'JobOffers' },
      ],
    }),

    // Placements ---------------------------------------------------------
    getPlacementById: builder.query<Placement, string>({
      query: (placementId) => ({
        url: `/jobs/placements/${placementId}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Placements', id: arg }]
    }),

    getPlacements: builder.query<PaginatedResponse<Placement>, {
      pageOptions: PageOptions;
      searchQuery?: string;
      employmentTypes?: EmploymentType[];
      owners?: string[];
      activeOnly?: boolean;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }>({
      query: ({ pageOptions, searchQuery, employmentTypes, owners, activeOnly, sortBy, sortOrder }) => ({
        url: `/jobs/placements`,
        params: {
          ...pageOptions,
          search_query: searchQuery || undefined,
          employment_type: employmentTypes?.length ? employmentTypes.join(',') : undefined,
          owner: owners?.length ? owners.join(',') : undefined,
          active: activeOnly ? 'true' : undefined,
          sort_by: sortBy || undefined,
          sort_order: sortBy ? sortOrder : undefined,
        }
      }),
      providesTags: ['Placements'],
    }),

    getPlacementOwners: builder.query<PlacementOwner[], void>({
      query: () => ({
        url: `/jobs/placements/owners`,
      }),
      providesTags: ['Placements'],
    }),

    getJobPlacements: builder.query<PaginatedResponse<Placement>, { jobId: string; params: PageOptions }>({
      query: ({ jobId, params }) => ({
        url: `/jobs/${jobId}/placements`,
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobPlacements', id: arg.jobId }]
    }),

    createPlacement: builder.mutation<APIResponse, { jobId: string; createDto: CreatePlacementDto }>({
      query: ({ jobId, createDto }) => ({
        url: `/jobs/${jobId}/placements`,
        method: HTTPRequestType.POST,
        data: createDto,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'JobPlacements', id: arg.jobId }, 'Placements']
    }),

    updatePlacement: builder.mutation<APIResponse, { placementId: string; updateDto: Partial<CreatePlacementDto> }>({
      query: ({ placementId, updateDto }) => ({
        url: `/jobs/placements/${placementId}`,
        method: HTTPRequestType.PUT,
        data: updateDto,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'JobPlacements' }, { type: 'Placements' }]
    }),

    addOfferToProfile: builder.mutation<APIResponse, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/jobs/applications/offers/${offerId}/add-to-profile`,
        method: HTTPRequestType.POST,
      })
    }),

    removePlacement: builder.mutation<APIResponse, string>({
      query: (placementId) => ({
        url: `/jobs/placements/${placementId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'JobPlacements' }]
    }),

    getPlacementDocuments: builder.query<PaginatedResponse<PlacementDocument>, { placementId: string, params: PageOptions }>({
      query: ({ placementId, params }) => ({
        url: `/jobs/placements/${placementId}/documents`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'PlacementDocuments', id: args.placementId }],
    }),
    createPlacementDocument: builder.mutation<APIResponse, { placementId: string, data: CreatePlacmentDocumentDto }>({
      query: ({ placementId, data }) => ({
        url: `/jobs/placements/${placementId}/documents`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'PlacementDocuments', id: args.placementId }],
    }),
    removePlacementDocument: builder.mutation<APIResponse, { placementId: string, doc_id: string }>({
      query: ({ placementId, doc_id }) => ({
        url: `/jobs/placements/${placementId}/documents/${doc_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'PlacementDocuments', id: args.placementId }],
    }),

    // Resource -----------------------------------------------------------
    initiateResourceRequest: builder.mutation<APIResponse, InitiateResourceRequestProps>({
      query: ({ applicationId, data }) => ({
        url: `/jobs/applications/${applicationId}/resource-requests`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ResourceRequests', id: arg.applicationId }]
    }),

    getIncomingApplicationResourceRequests: builder.query<
      PaginatedResponse<ApplicationResourceRequest>,
      { applicationId: string; params: PageOptions }
    >({
      query: ({ applicationId, params }) => ({
        url: `/jobs/applications/${applicationId}/resource-requests/incoming`,
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ResourceRequests', id: arg.applicationId }]
    }),

    getOutgoingApplicationResourceRequests: builder.query<
      PaginatedResponse<ApplicationResourceRequest>,
      { applicationId: string; params: PageOptions }
    >({
      query: ({ applicationId, params }) => ({
        url: `/jobs/applications/${applicationId}/resource-requests/outgoing`,
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ResourceRequests', id: arg.applicationId }]
    }),

    actionResourceRequest: builder.mutation<APIResponse, ActionResourceRequestProps>({
      query: ({ requestId, data }) => ({
        url: `/jobs/applications/resource-requests/${requestId}/action`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['ResourceRequests'],
    }),

    // insights
    getApplicationInsights: builder.query<ApplicationInsight[], string>({
      query: (applicationId) => ({
        url: `/jobs/applications/${applicationId}/insights`,
      }),
    }),

    importApplicationProfile: builder.mutation<APIResponse, { applicationId: string; documentId: string; options?: { add_to_profile: boolean } }>({
      query: ({ applicationId, documentId, options = { add_to_profile: false } }) => ({
        url: `/jobs/applications/${applicationId}/candidate-profile/import`,
        method: HTTPRequestType.POST,
        data: {
          document_id: documentId,
          ...options,
        },
      }),
      // Documents: the import can link a resume from the candidate record onto the application.
      invalidatesTags: (_result, _error, arg) => [{ type: 'ApplicationProfile', id: arg.applicationId }, 'Profile', 'Documents']
    }),

    // compliance
    getApplicationComplianceSubmissions: builder.query<ComplianceSubmission[], { applicationId: string; jobId: string }>({
      query: ({ applicationId, jobId }) => ({
        url: `/jobs/${jobId}/applications/${applicationId}/compliance/submissions`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ScreeningQuestionSubmissions', id: arg.applicationId }]
    }),
    updateSubmissionVisibility: builder.mutation<APIResponse, { applicationId: string; submissionId: string; visibilityStatus: SubmissionVisibilityStatus }>({
      query: ({ applicationId, submissionId, visibilityStatus }) => ({
        url: `/jobs/applications/${applicationId}/compliance/submissions/${submissionId}/visibility`,
        method: HTTPRequestType.POST,
        data: {
          status: visibilityStatus
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ScreeningQuestionSubmissions', id: arg.applicationId }]
    }),
    updateAllSubmissionVisibility: builder.mutation<APIResponse, { applicationId: string; visibilityStatus: SubmissionVisibilityStatus }>({
      query: ({ applicationId, visibilityStatus }) => ({
        url: `/jobs/applications/${applicationId}/compliance/submissions/visibility`,
        method: HTTPRequestType.POST,
        data: {
          status: visibilityStatus
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ScreeningQuestionSubmissions', id: arg.applicationId }]
    }),

    saveDocumentToProfile: builder.mutation<APIResponse, { applicationId: string; documentId: string }>({
      query: ({ applicationId, documentId }) => ({
        url: `/jobs/applications/${applicationId}/documents/save-to-profile`,
        method: HTTPRequestType.POST,
        data: {
          document_id: documentId
        },
      }),
    }),

  }),
});

export const {
  useGetApplicationsQuery,
  useGetApplicationsLeanQuery,
  useGetJobApplicantsQuery,
  useGetJobCandidatesQuery,
  useLazyGetJobCandidatesQuery,
  useGetPendingApplicantsQueryQuery,
  useResendApplyIntentVerificationMutation,
  useProgressCandidateApplicationMutation,
  useProgressToCandidateMutation,
  useShortlistApplicationsMutation,
  useWithdrawApplicationMutation,
  useUpdateApplicationRatingMutation,
  useUpdateApplicationOpenStatusMutation,
  useGetApplicationActivitiesQuery,
  useRejectApplicationsMutation,
  useRequestRejectApplicationsMutation,
  useGetApplicationByIdQuery,
  useLazyGetApplicationByIdQuery,
  useRequestApplicationStageChangeMutation,
  useGetApplicationStageChangeRequestsQuery,
  useGetApplicationStageChangeRequestByIdQuery,
  useActionStageChangeRequestMutation,
  useGetAllJobStageChangeRequestsQuery,

  useCreateOfferMutation,
  useUpdateOfferMutation,
  useGetApplicationOffersQuery,
  useGetOfferByIdQuery,
  useGetMyOffersQuery,
  useActionOfferMutation,
  useLogOfferActionMutation,
  useGetApplicationCandidateProfileQuery,
  useUpdateApplicationCandidateProfileMutation,
  useUpdateApplicationContactMutation,
  useApproveOfferMutation,
  useNotifyOfferCandidateMutation,
  useRequestOfferChangesMutation,
  useRescindOfferMutation,
  useGetOffersByJobIdQuery,

  useGetPlacementsQuery,
  useGetPlacementOwnersQuery,
  useGetPlacementByIdQuery,
  useGetJobPlacementsQuery,
  useCreatePlacementMutation,
  useUpdatePlacementMutation,
  useRemovePlacementMutation,
  useAddOfferToProfileMutation,
  useGetPlacementDocumentsQuery,
  useCreatePlacementDocumentMutation,
  useRemovePlacementDocumentMutation,

  useInitiateResourceRequestMutation,
  useGetIncomingApplicationResourceRequestsQuery,
  useGetOutgoingApplicationResourceRequestsQuery,
  useActionResourceRequestMutation,

  useGetApplicationInsightsQuery,

  useGetApplicationComplianceSubmissionsQuery,
  useUpdateSubmissionVisibilityMutation,
  useUpdateAllSubmissionVisibilityMutation,

  useImportApplicationProfileMutation,

  useSaveDocumentToProfileMutation,
} = applicationsApi;
